import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { getCardSignals, getCardData, ButtonState } from "../store/collection-store";
import { startModalChain } from "../store/modal-store";
import { isVoucherProductTags, shouldMergeVouchers } from "../store/voucher-store";
import { buildVariantUrl } from "../utils/knocking";

/**
 * Reactive add-to-cart / sold-out / state button for collection cards.
 *
 * Always type="button" — bypasses product-form.js entirely.
 * All cart operations go through modal orchestrator for consistent modal handling
 * (holiday shipping, custom text, etc.), then cart-store.ts updates the reactive cart.
 *
 * Attributes (translated strings come from Liquid `t` filter):
 *   data-card-id              — matches the card registered in collection-store
 *   data-product-base-url     — base product URL for navigation states (no query string)
 *   data-text-add-to-cart
 *   data-text-sold-out
 *   data-text-extended-offer
 *   data-text-click-to-buy
 *   data-text-view-more
 *   data-extra-classes        — extra CSS classes on the button (e.g. "product-has-variants")
 */
@customElement("card-button")
export class CardButton extends LitElement {
  @property({ type: String, attribute: "data-card-id" }) cardId = "";
  @property({ type: String, attribute: "data-product-base-url" }) productBaseUrl = "";

  @property({ type: String, attribute: "data-text-add-to-cart" }) textAddToCart = "Add to Cart";
  @property({ type: String, attribute: "data-text-sold-out" }) textSoldOut = "Sold Out";
  @property({ type: String, attribute: "data-text-extended-offer" }) textExtendedOffer = "Notify Me";
  @property({ type: String, attribute: "data-text-click-to-buy" }) textClickToBuy = "Select Options";
  @property({ type: String, attribute: "data-text-view-more" }) textViewMore = "View More";
  @property({ type: String, attribute: "data-extra-classes" }) extraClasses = "";

  @state() private _buttonState: ButtonState = "addToCart";
  @state() private _loading = false;
  @state() private _showLimitNotice = false;

  private _disposeEffect?: () => void;
  private _limitNoticeTimer: ReturnType<typeof setTimeout> | null = null;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();

    const signals = getCardSignals(this.cardId);
    if (!signals) {
      console.error(`card-button: no signals for card "${this.cardId}"`);
      return;
    }

    this._disposeEffect = effect(() => {
      this._buttonState = signals.buttonState.value;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeEffect?.();
    if (this._limitNoticeTimer !== null) clearTimeout(this._limitNoticeTimer);
  }

  private _triggerLimitNotice() {
    if (this._limitNoticeTimer !== null) clearTimeout(this._limitNoticeTimer);
    this._showLimitNotice = true;
    this._limitNoticeTimer = setTimeout(() => {
      this._showLimitNotice = false;
      this._limitNoticeTimer = null;
    }, 3000);
  }

  private get _label(): string {
    switch (this._buttonState) {
      case "addToCart":
      case "limitReached": return this.textAddToCart;
      case "soldOut": return this.textSoldOut;
      case "extendedOffer-modal":
      case "extendedOffer-redirect": return this.textExtendedOffer;
      case "clickToBuy": return this.textClickToBuy;
      case "viewMore": return this.textViewMore;
    }
  }

  /** Read all properties[*] hidden inputs from the parent form. */
  private _collectProperties(): Record<string, string> {
    const form = this.closest("form");
    if (!form) return {};
    const props: Record<string, string> = {};
    for (const input of Array.from(
      form.querySelectorAll<HTMLInputElement>('input[name^="properties["]')
    )) {
      const match = input.name.match(/properties\[(.+)\]/);
      if (match && input.value) props[match[1]] = input.value;
    }
    return props;
  }

  /** Navigate to the product URL with the currently selected variant. */
  private _navigateToProduct() {
    const signals = getCardSignals(this.cardId);
    const variantId = signals?.selectedVariantId.value;
    const base = this.productBaseUrl;
    if (!base) return;
    window.location.href = buildVariantUrl(base, variantId);
  }

  private async _handleClick(e: Event) {
    e.preventDefault(); // stop <a> navigation AND form submission for all states

    if (this._loading) return;

    switch (this._buttonState) {
      case "limitReached": {
        this._triggerLimitNotice();
        break;
      }

      case "addToCart": {
        const signals = getCardSignals(this.cardId);
        const productData = getCardData(this.cardId);
        const variantId = signals?.selectedVariantId.value;
        const variant = signals?.selectedVariant.value;

        if (!variantId) return;

        const properties = this._collectProperties();
        if (signals?.shipsBy) {
          properties["_ships-by"] = signals.shipsBy;
        }

        const button = e.target as HTMLButtonElement;

        const rawImageSrc = variant?.featured_image?.src || productData?.featured_image || "";
        const imageUrl = rawImageSrc.startsWith("//") ? `https:${rawImageSrc}` : rawImageSrc;

        const tags = signals?.tags;
        const isVoucher = isVoucherProductTags(tags);

        this._loading = true;
        try {
          const added = await startModalChain({
            productId: signals?.productId,
            variantId: variantId,
            quantity: 1,
            productTitle: productData?.title,
            variantTitle: variant?.title || variant?.name,
            customization: properties,
            triggerButton: button,
            vendor: signals?.vendor || "",
            source: "product-card",
            imageUrl,
            isVoucher,
            variantSku: isVoucher ? variant?.sku : undefined,
            mergeVouchers: isVoucher ? shouldMergeVouchers(tags) : undefined,
          });
          // Safety net: if the add was blocked (e.g. a limit reached before the
          // button state updated), surface the limit notice instead of failing silently.
          if (!added) this._triggerLimitNotice();
        } finally {
          this._loading = false;
        }
        break;
      }

      case "clickToBuy":
      case "viewMore":
      case "extendedOffer-redirect":
        this._navigateToProduct();
        break;

      case "extendedOffer-modal": {
        // Get variant and product data from collection store
        const signals = getCardSignals(this.cardId);
        const productData = getCardData(this.cardId);
        const variant = signals?.selectedVariant.value;

        // Dispatch event with variant/product data for the extended-offer modal
        this.dispatchEvent(
          new CustomEvent("open-extended-offer", {
            bubbles: true,
            composed: true,
            detail: {
              variant: variant,
              product: productData,
            },
          })
        );
        break;
      }

      case "soldOut":
        // No action
        break;
    }
  }

  render() {
    const isDisabled = this._buttonState === "soldOut" || this._loading;
    const isExtendedOfferState =
      this._buttonState === "extendedOffer-modal" ||
      this._buttonState === "extendedOffer-redirect";

    const baseClasses = [
      "product-form__submit",
      "button",
      "w-full",
      "btn",
      "min-h-[48px]",
      "rounded-l-none",
      isExtendedOfferState
        ? "bg-[var(--color-secondary-500)] hover:bg-[var(--color-secondary-500)] text-white"
        : "",
      this.extraClasses,
    ]
      .filter(Boolean)
      .join(" ");

    // Derive limit type from signals for the notice text
    const signals = getCardSignals(this.cardId);
    const limitType = signals?.productId
      ? (window as any).productLimits?.[signals.productId.toString()] !== undefined
        ? "product"
        : (window as any).brandLimits?.[signals.vendor] !== undefined
          ? "brand"
          : "product"
      : "product";

    // No dynamic child bindings inside <button>.
    // Vanilla product-card.js calls button.textContent = "..." which destroys
    // any text nodes Lit creates inside the button, causing null-ref errors on
    // the next render cycle. Text is set imperatively in updated() instead.
    return html`
      <button
        type="button"
        name="add"
        class=${baseClasses}
        data-button-location="product-card"
        ?disabled=${isDisabled}
        @click=${this._handleClick}
      ></button>
      <div
        class="absolute inset-0 z-10 flex items-center justify-center rounded-sm bg-[var(--color-danger-200)] px-5 text-center transition-opacity duration-300 ease-out ${this._showLimitNotice ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}"
      >
        <span class="text-sm text-[var(--color-danger-950)]"
          >You've reached the limit for this ${limitType}</span>
      </div>
    `;
  }

  updated() {
    const btn = this.querySelector<HTMLButtonElement>("button.product-form__submit");
    if (!btn) return;
    if (this._loading) {
      btn.innerHTML = `
        <svg aria-hidden="true" focusable="false" class="animate-spin w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.25)" stroke-width="2.5"></circle>
          <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.9)" stroke-width="2.5" stroke-linecap="round" stroke-dasharray="62.83" stroke-dashoffset="47"></circle>
        </svg>`;
    } else {
      btn.textContent = this._label;
    }
  }
}
